import clsx from "clsx";
import { useEffect, useMemo } from "react";
import type { FieldValues, Path } from "react-hook-form";

import CurrencyInput from "~/components/formElements/CurrencyInput";
import FormInputError from "~/components/formElements/FormInputError";
import Switch from "~/components/formElements/Switch/Switch.tsx";
import Headline from "~/components/Headline/Headline";
import InvoiceTotalsDetailLabel
	from "~/modules/billing/components/InvoiceTotalsDetail/components/InvoiceTotalsDetailLabel";
import InvoiceTotalsDetailValue
	from "~/modules/billing/components/InvoiceTotalsDetail/components/InvoiceTotalsDetailValue";
import InvoiceTotalsDetail from "~/modules/billing/components/InvoiceTotalsDetail/InvoiceTotalsDetail";
import { formatNumberToCurrency } from "~/utils/currencyUtils.ts";

import type { BaseInvoiceTotalsFormSectionType, InvoiceTotalsFormSectionProps } from "./invoiceTotalsFormSectionTypes";

const InvoiceTotalsFormSection = <T extends FieldValues & BaseInvoiceTotalsFormSectionType>({
	control,
	errorMessage,
	watch,
	setValue,
	title,
	includeTravelCosts = true,
	allowGrossInput = false,
	readOnlySubtotal = false,
}: InvoiceTotalsFormSectionProps<T>) => {
	const subtotalCents = watch("subtotalCents" as Path<T>);
	const vatPercentage = watch("vatPercentage" as Path<T>);
	const totalAmountCents = watch("totalAmountCents" as Path<T>);
	const travelCostCentsBeforeVat = includeTravelCosts && !readOnlySubtotal
		? watch("travelCostCentsBeforeVat" as Path<T>)
		: 0;

	const isGrossBased = watch("isGrossBased" as Path<T>);

	const [invoiceSubTotalWithTravelCostsCents, vatCents, derivedTotalAmountCents, derivedSubtotalCents] = useMemo(() => {
		if (isGrossBased) {
			const vatCents = Math.sign(totalAmountCents || 0) * Math.round(Math.abs(totalAmountCents || 0) * (vatPercentage || 0) / (10000 + (vatPercentage || 0)));
			const derivedSubtotalCents = (totalAmountCents || 0) - vatCents - (travelCostCentsBeforeVat || 0);
			return [totalAmountCents || 0, vatCents, totalAmountCents || 0, derivedSubtotalCents];
		}

		const invoiceSubTotalWithTravelCostsCents = subtotalCents + (travelCostCentsBeforeVat || 0);
		const vatCents = Math.sign(invoiceSubTotalWithTravelCostsCents) * Math.round(Math.abs(invoiceSubTotalWithTravelCostsCents) * (vatPercentage || 0) / 10000);
		const derivedTotalAmountCents = invoiceSubTotalWithTravelCostsCents + vatCents;
		return [invoiceSubTotalWithTravelCostsCents, vatCents, derivedTotalAmountCents, subtotalCents];
	}, [isGrossBased, subtotalCents, travelCostCentsBeforeVat, vatPercentage, totalAmountCents]);

	useEffect(() => {
		if (isGrossBased) {
			setValue("subtotalCents" as Path<T>, derivedSubtotalCents as any);
		} else {
			setValue("totalAmountCents" as Path<T>, derivedTotalAmountCents as any);
		}
	}, [isGrossBased, derivedTotalAmountCents, derivedSubtotalCents, setValue]);

	return (
		<div className="col-span-2 grid grid-cols-2 gap-x-4">
			<div className={clsx("col-span-2 flex justify-between", allowGrossInput && "mb-2")}>
				{title && <Headline type="h4">{title}</Headline>}
				{allowGrossInput && (
					<Switch
						className="ml-auto"
						control={control}
						name="isGrossBased"
						inputSize="sm"
						labelOn="Butto-basiert"
						labelOff="Netto-basiert"
						showAllLabels={true}
					/>
				)}
			</div>

			{!isGrossBased && !readOnlySubtotal && (
				<InvoiceTotalsDetail label="Summe Einzelpositionen">
					<CurrencyInput
						name="subtotalCents"
						control={control}
						textAlign="right"
						inputSize="sm"
					/>
				</InvoiceTotalsDetail>
			)}

			{(isGrossBased || readOnlySubtotal) && (
				<InvoiceTotalsDetail label="Summe Einzelpositionen">
					<div className="pr-1.5 text-right text-sm">
						{formatNumberToCurrency(readOnlySubtotal ? subtotalCents : derivedSubtotalCents)}
					</div>
				</InvoiceTotalsDetail>
			)}

			{includeTravelCosts && !readOnlySubtotal && (
				<InvoiceTotalsDetail label="Reisekosten">
					<CurrencyInput
						name="travelCostCentsBeforeVat"
						control={control}
						textAlign="right"
						inputSize="sm"
					/>
				</InvoiceTotalsDetail>
			)}

			<div className="col-span-2 mt-1 h-px w-full bg-gray-200" />

			<InvoiceTotalsDetail label="Rechnungssumme">
				<div className="pr-1.5 text-right text-sm">
					{formatNumberToCurrency(invoiceSubTotalWithTravelCostsCents)}
				</div>
			</InvoiceTotalsDetail>

			<InvoiceTotalsDetailLabel>
				<span>MwSt</span>
				<div className="mx-2 w-16">
					<CurrencyInput
						name="vatPercentage"
						textAlign="right"
						min={0}
						maxValue={10000}
						control={control}
						inputSize="sm"
					/>
				</div>
				<span>%</span>
			</InvoiceTotalsDetailLabel>

			<InvoiceTotalsDetailValue>
				<div className="pr-1.5 text-right text-sm">
					{formatNumberToCurrency(vatCents)}
				</div>
			</InvoiceTotalsDetailValue>

			<div className="col-span-2 mt-1 h-px w-full bg-gray-200" />

			<InvoiceTotalsDetailLabel bold={true}>
				Gesamt
			</InvoiceTotalsDetailLabel>

			{isGrossBased ? (
				<InvoiceTotalsDetailValue bold={true}>
					<CurrencyInput
						name="totalAmountCents"
						control={control}
						textAlign="right"
						inputSize="sm"
					/>
				</InvoiceTotalsDetailValue>
			) : (
				<InvoiceTotalsDetailValue bold={true}>
					<div className="pr-1.5 text-right text-sm">
						{formatNumberToCurrency(derivedTotalAmountCents)}
					</div>
				</InvoiceTotalsDetailValue>
			)}

			{errorMessage && <div className="col-span-2"><FormInputError>{errorMessage}</FormInputError></div>}
		</div>
	);
};

export default InvoiceTotalsFormSection;